import { useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { IRideDto, rideSelectedPassengersIdsAF } from "@ryde/shared";

import { RideBulkActionModeEnum } from "./ride-station-bulk-actions.models";
import { isRideBulkMoveAllowedFromRide } from "./ride-station-bulk-move.utils";

export const useRideStationBulkActionsMode = (rideDto: Partial<IRideDto>) => {
  const selectedPassengersIds = useRecoilValue(
    rideSelectedPassengersIdsAF(rideDto?.id || 0),
  );
  const [activeMode, setActiveMode] = useState<RideBulkActionModeEnum>(
    RideBulkActionModeEnum.Menu,
  );
  const updateStationsButtonRef = useRef<HTMLButtonElement>(null);
  const movePassengersButtonRef = useRef<HTMLButtonElement>(null);
  const panelBackButtonRef = useRef<HTMLButtonElement>(null);
  /** The panel we are returning from, so its menu entry can take focus back. */
  const returnFocusModeRef = useRef<RideBulkActionModeEnum | undefined>(
    undefined,
  );

  const showMenu = useEvent(() => {
    returnFocusModeRef.current = activeMode;
    setActiveMode(RideBulkActionModeEnum.Menu);
  });

  const showUpdateStations = useEvent(() => {
    returnFocusModeRef.current = undefined;
    setActiveMode(RideBulkActionModeEnum.UpdateStations);
  });

  const showMovePassengers = useEvent(() => {
    returnFocusModeRef.current = undefined;
    setActiveMode(RideBulkActionModeEnum.MovePassengers);
  });

  const isMovePassengersAllowed = useMemo(() => {
    return isRideBulkMoveAllowedFromRide(rideDto);
  }, [rideDto]);

  useEffect(() => {
    if (!selectedPassengersIds?.length) {
      returnFocusModeRef.current = undefined;
      setActiveMode(RideBulkActionModeEnum.Menu);
    }
  }, [selectedPassengersIds?.length]);

  // Switching panels unmounts the focused control, so focus has to be placed by hand.
  useEffect(() => {
    if (activeMode !== RideBulkActionModeEnum.Menu) {
      panelBackButtonRef.current?.focus();
      return;
    }

    if (returnFocusModeRef.current === RideBulkActionModeEnum.UpdateStations) {
      updateStationsButtonRef.current?.focus();
    } else if (
      returnFocusModeRef.current === RideBulkActionModeEnum.MovePassengers
    ) {
      movePassengersButtonRef.current?.focus();
    }

    returnFocusModeRef.current = undefined;
  }, [activeMode]);

  return {
    selectedPassengersIds,
    activeMode,
    isMovePassengersAllowed,
    updateStationsButtonRef,
    movePassengersButtonRef,
    panelBackButtonRef,
    showMenu,
    showUpdateStations,
    showMovePassengers,
  };
};
